import React, { FC, useState } from 'react';
import type {
  OrganisationGroup,
  OrganisationMemberRole,
  OrganisationSeatKind,
  OrganisationSeatPlan,
} from '@dreamai/types';
import { ReactComponent as SendIcon } from '../../icons/action-send.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { formatMinor } from './organisationCreditLabels';
import OrganisationModal from './OrganisationModal';

export type InviteRole = OrganisationMemberRole;

export interface InviteMemberInput {
  recipient: string;
  name?: string;
  seatKind: OrganisationSeatKind;
  /** Admin rights are granted when they accept; `member` is a seat only */
  role: InviteRole;
  /** Groups the member is placed in as soon as their membership exists */
  groupIds?: string[];
}

interface InviteMemberModalProps {
  groups: OrganisationGroup[];
  /** Groups are switched on for this organisation (the backend lists them) */
  groupsAvailable: boolean;
  /** Price of one Personal Assistant seat, once billing has loaded */
  seatPlan: OrganisationSeatPlan | null;
  submitting: boolean;
  /** Resolves to an error message, or null once the invitation is sent */
  onInvite: (input: InviteMemberInput) => Promise<string | null>;
  onClose: () => void;
}

const ROLES: ReadonlyArray<{
  id: InviteRole;
  label: string;
  description: string;
}> = [
  {
    id: 'admin',
    label: 'Admin',
    description: 'Admins can manage and invite users, and change org settings',
  },
  {
    id: 'member',
    label: 'Member',
    description: 'Members cannot access organisation settings',
  },
];

const InviteMemberModal: FC<InviteMemberModalProps> = ({
  groups,
  groupsAvailable,
  seatPlan,
  submitting,
  onInvite,
  onClose,
}) => {
  const [recipient, setRecipient] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [invalidRecipient, setInvalidRecipient] = useState(false);
  // Only Personal Assistant seats are offered for now.
  const seatKind: OrganisationSeatKind = 'assistant';

  const toggleGroup = (groupId: string, checked: boolean) =>
    setGroupIds((prev) =>
      checked
        ? [...prev.filter((id) => id !== groupId), groupId]
        : prev.filter((id) => id !== groupId),
    );

  const submit = async () => {
    const trimmed = recipient.trim();
    if (!trimmed) {
      setInvalidRecipient(true);
      setError('Add an email address or phone number to invite');
      return;
    }
    setInvalidRecipient(false);
    setError(
      await onInvite({
        recipient: trimmed,
        name: name.trim() || undefined,
        seatKind,
        role,
        ...(groupIds.length > 0 ? { groupIds } : {}),
      }),
    );
  };

  return (
    <OrganisationModal
      icon={<SendIcon />}
      title='Invite member'
      error={error}
      busy={submitting}
      testId='invite-member-modal'
      onClose={onClose}
      onSubmit={submit}
      actions={
        <>
          <button
            type='button'
            className='organisation-modal-cancel'
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='organisation-modal-primary'
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
        </>
      }
    >
      <div className='organisation-modal-panel'>
        <label className='organisation-modal-field'>
          <span className='organisation-modal-label'>
            Email or phone number
          </span>
          <input
            type='text'
            className='organisation-modal-input'
            value={recipient}
            autoFocus
            autoComplete='off'
            disabled={submitting}
            aria-invalid={invalidRecipient}
            onChange={(e) => {
              setRecipient(e.target.value);
              if (invalidRecipient) {
                setInvalidRecipient(false);
                setError(null);
              }
            }}
          />
        </label>
        <label className='organisation-modal-field'>
          <span className='organisation-modal-label'>Name (Optional)</span>
          <input
            type='text'
            className='organisation-modal-input'
            value={name}
            autoComplete='off'
            disabled={submitting}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className='organisation-modal-field'>
          <span className='organisation-modal-label'>Plan</span>
          <div className='organisation-modal-select'>
            <span className='organisation-modal-select-value'>
              AI Secretary
            </span>
            {seatPlan && (
              <span className='organisation-modal-select-price'>
                {formatMinor(seatPlan.unitAmountMinor, seatPlan.currency)}
                /month
              </span>
            )}
            <ChevronDownIcon aria-hidden='true' />
            <select
              aria-label='Plan'
              value={seatKind}
              disabled={submitting}
              onChange={() => undefined}
            >
              <option value='assistant'>AI Secretary</option>
            </select>
          </div>
        </label>
        {groupsAvailable && groups.length > 0 && (
          <fieldset
            className='organisation-modal-groups'
            data-testid='invite-member-groups'
            disabled={submitting}
          >
            <legend className='organisation-modal-label'>
              Groups (Optional)
            </legend>
            {groups.map((group) => (
              <label key={group.id}>
                <input
                  type='checkbox'
                  checked={groupIds.includes(group.id)}
                  onChange={(e) => toggleGroup(group.id, e.target.checked)}
                />
                {group.name}
              </label>
            ))}
          </fieldset>
        )}
        <fieldset className='organisation-modal-roles' disabled={submitting}>
          <legend className='organisation-modal-label'>Invite as</legend>
          {ROLES.map((r) => (
            <label
              key={r.id}
              className={`organisation-modal-role${role === r.id ? ' selected' : ''}`}
            >
              <input
                type='radio'
                name='invite-role'
                value={r.id}
                checked={role === r.id}
                onChange={() => setRole(r.id)}
              />
              <span className='organisation-modal-radio' aria-hidden='true' />
              <strong>{r.label}</strong>
              <span>{r.description}</span>
            </label>
          ))}
        </fieldset>
      </div>
    </OrganisationModal>
  );
};

export default InviteMemberModal;
